import { supabase } from "@/lib/supabase";
import type { Occupation } from "@/core";

/**
 * Adding somebody to a household.
 *
 * The person and their opening terms are written by one function so they
 * arrive together — see migration 0030. An employees row without terms is
 * somebody who cannot clock in and has no way of finding out why, because from
 * their side the app just refuses.
 */

export interface NewEmployee {
  orgId: string;
  siteId: string;
  fullName: string;
  phone: string;
  occupation: Occupation;
  /** Only when occupation is "other" — their own words for the job. */
  occupationOther?: string | null;
  nationality: string;
  startedOn: string;
  /** In major units — rands, not cents. Converted on the way down. */
  rate: number;
  daysPerWeek: number;
}

export type AddResult =
  | { ok: true; employeeId: string }
  | {
      ok: false;
      reason: "NOT_PERMITTED" | "NO_PAY_PERMISSION" | "BAD_DETAILS"
            | "DUPLICATE_PHONE" | "NETWORK";
    };

export async function addEmployee(input: NewEmployee): Promise<AddResult> {
  const { data, error } = await supabase.rpc("add_employee", {
    p_org: input.orgId,
    p_site: input.siteId,
    p_full_name: input.fullName,
    p_phone: input.phone,
    p_occupation: input.occupation,
    p_occupation_other: input.occupationOther ?? null,
    p_nationality: input.nationality,
    p_started_on: input.startedOn,
    // Money is stored in cents so it is never a float in the database.
    p_rate_cents: Math.round(input.rate * 100),
    p_days_per_week: Math.round(input.daysPerWeek),
  });

  if (!error) return { ok: true, employeeId: data as string };

  if (error.code === "23505") return { ok: false, reason: "DUPLICATE_PHONE" };
  if (error.code === "23514") return { ok: false, reason: "BAD_DETAILS" };
  if (error.code === "42501") {
    // Two different refusals wear the same code, and they need different
    // answers: one is "you cannot do this", the other is "ask the owner to set
    // the rate". Telling them apart from the message is the only way.
    return {
      ok: false,
      reason: (error.message ?? "").includes("set pay") ? "NO_PAY_PERMISSION" : "NOT_PERMITTED",
    };
  }
  return { ok: false, reason: "NETWORK" };
}

/** Whether this account may add somebody, and set what they are paid. */
export async function mayAddPeople(orgId: string): Promise<{ people: boolean; pay: boolean }> {
  const [people, pay] = await Promise.all([
    supabase.rpc("may", { target_org: orgId, perm: "manage_people" }),
    supabase.rpc("may", { target_org: orgId, perm: "change_terms" }),
  ]);
  return { people: Boolean(people.data), pay: Boolean(pay.data) };
}
